import React from "react";

import { isEmpty } from "lodash/fp";

import Typography from "@mui/material/Typography";

import Contributors from "@app/analytics/Pulse/components/Contributors";
import SessionListPopover from "@app/analytics/Pulse/components/SessionListPopover";
import usePulseClips from "@app/analytics/Pulse/hooks/usePulseClips";
import { getAllContributors, getAllSessions } from "@app/analytics/Pulse/utils";
import { pluralizePhrase } from "@app/common/utils";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { usePageTitle } from "@app/utils/hooks";

import Stack, { Row } from "@rui/Stack";

import NotEnoughData from "./NotEnoughData";
import NoAccess from "../components/NoAccess";
import { Content } from "../components/styles";
import Summary from "../components/Summary";
import TopicsTable from "../components/TopicsTable";
import useForYourWorkspace from "../hooks/useForYourWorkspace";
import usePulseStore from "../stores/pulse";

const ForYourWorkspace = () => {
  usePageTitle("For You - Workspace");

  const {
    workspaceQuery,
    isLoading: workspaceResultsLoading,
    hasPermissionError,
  } = useForYourWorkspace();
  const { isInitialLoading: isClipsLoading } = usePulseClips();

  const { workspace } = useWorkspace();

  const selectedRange = usePulseStore(
    (state) => state.forYourWorkspace.selectedRange
  );

  const isLoading = workspaceResultsLoading || isClipsLoading;
  const result = workspaceQuery.data;
  const hasNoResult = !isLoading && isEmpty(result);

  const contributors = getAllContributors(result);
  const sessions = getAllSessions(result);

  if (hasPermissionError) {
    return <NoAccess type="workspace" />;
  }

  return (
    <Content height="100%" sx={{ padding: 2 }}>
      <Stack spacing={2} flex={1}>
        {hasNoResult ? (
          <NotEnoughData />
        ) : (
          <>
            <Summary
              title={workspace.name}
              subtitle={`Here's what happened in your Workspace ${selectedRange.label}`}
              content={result?.summary}
              thumbnail={{ src: result?.thumbnailUrl }}
              loading={isLoading}
              details={
                <Row variant="right" spacing={2}>
                  <SessionListPopover sessions={sessions}>
                    <Typography
                      variant="bodySecondaryMed"
                      color="shades.neutral.400"
                    >
                      {pluralizePhrase(sessions.length, "meeting")} tagged (
                      {selectedRange.label})
                    </Typography>
                  </SessionListPopover>
                  <Contributors contributors={contributors} maxVisible={5} />
                </Row>
              }
            />
            <TopicsTable
              topics={result?.pulseTopics ?? []}
              loading={isLoading}
            />
          </>
        )}
      </Stack>
    </Content>
  );
};

export default ForYourWorkspace;
